import type {
  CanonicalTreeGeometryRoleResult,
} from './CanonicalTreeGeometryRoleAuthority';

export type CanonicalTreeRuntimeAuthorityConvergenceStatus =
  | 'READY'
  | 'BLOCKED';

export type CanonicalTreeRuntimeAuthorityConvergenceBlocker =
  | 'VIEWER_PROJECTION_UNAVAILABLE'
  | 'PRESENTATION_EVIDENCE_UNRESOLVED'
  | 'PRESENTATION_EVIDENCE_CONFLICTING'
  | 'COMPATIBILITY_EVIDENCE_INVALID'
  | 'VISIBLE_MEMBER_PROJECTION_MISSING'
  | 'VISIBLE_MEMBER_GEOMETRY_ROLE_MISSING'
  | 'BRANCH_AUTHORITY_LEGACY_FALLBACK'
  | 'BRANCH_AUTHORITY_BLOCKED'
  | 'LAYOUT_PATH_LEGACY_FALLBACK'
  | 'LAYOUT_PATH_BLOCKED'
  | 'TOPOLOGY_LEGACY_FALLBACK'
  | 'TOPOLOGY_REPLACEMENT_BLOCKED'
  | 'CONNECTOR_EDGE_MISSING'
  | 'VISIBLE_MEMBER_SET_MISMATCH';

export interface CanonicalTreeRuntimePresentationEvidence {
  readonly hasProjection: boolean;
  readonly reachableMemberIds:
    ReadonlySet<string>;
  readonly treeMemberIds:
    ReadonlySet<string>;
  readonly unresolvedEvidenceCount: number;
  readonly conflictingEvidenceCount: number;
  readonly invalidCompatibilityEvidenceCount: number;
}

export interface CanonicalTreeRuntimeBranchEvidence {
  readonly source:
    | 'canonical'
    | 'legacy-fallback';
  readonly readiness:
    | 'READY'
    | 'READY-WITH-CANONICAL-ADDITIONS'
    | 'BLOCKED';
}

export interface CanonicalTreeRuntimeLayoutPathEvidence {
  readonly source:
    | 'canonical-compatible'
    | 'legacy-fallback';
  readonly readiness:
    | 'READY'
    | 'READY-WITH-CANONICAL-ADDITIONS'
    | 'BLOCKED';
}

export interface CanonicalTreeRuntimeTopologyEvidence {
  readonly selectedSource:
    | 'canonical-compatible'
    | 'legacy-fallback';
  readonly readiness:
    | 'SAFE'
    | 'BLOCKED';
}

export interface CanonicalTreeRuntimeAuthorityConvergenceInput {
  readonly presentation:
    CanonicalTreeRuntimePresentationEvidence;
  readonly geometry:
    CanonicalTreeGeometryRoleResult;
  readonly branch:
    CanonicalTreeRuntimeBranchEvidence;
  readonly layoutPath:
    CanonicalTreeRuntimeLayoutPathEvidence;
  readonly topology:
    CanonicalTreeRuntimeTopologyEvidence;
  readonly canonicalEdgeIds:
    ReadonlySet<string>;
}

export interface CanonicalTreeRuntimeAuthorityConvergenceResult {
  readonly status:
    CanonicalTreeRuntimeAuthorityConvergenceStatus;
  readonly blockers:
    readonly CanonicalTreeRuntimeAuthorityConvergenceBlocker[];
  readonly productionVisibleMemberIds:
    readonly string[];
  readonly missingProjectionMemberIds:
    readonly string[];
  readonly missingGeometryRoleMemberIds:
    readonly string[];
  readonly missingConnectorEdgeIds:
    readonly string[];
  readonly permitsLegacyFallback: false;
  readonly permitsPartialCanonicalRendering: false;
  readonly mutatesCanonicalGraph: false;
}

const ready = (
  readiness:
    | 'READY'
    | 'READY-WITH-CANONICAL-ADDITIONS'
    | 'BLOCKED',
): boolean =>
  readiness === 'READY' ||
  readiness ===
    'READY-WITH-CANONICAL-ADDITIONS';

const sorted = (
  values: Iterable<string>,
): readonly string[] =>
  Object.freeze(
    Array.from(new Set(values)).sort(),
  );

export const resolveCanonicalTreeActiveRuntimeAuthorityConvergence = (
  input:
    CanonicalTreeRuntimeAuthorityConvergenceInput,
): CanonicalTreeRuntimeAuthorityConvergenceResult => {
  const blockers =
    new Set<
      CanonicalTreeRuntimeAuthorityConvergenceBlocker
    >();

  const productionVisibleMemberIds =
    sorted(input.presentation.treeMemberIds);

  if (!input.presentation.hasProjection) {
    blockers.add(
      'VIEWER_PROJECTION_UNAVAILABLE',
    );
  }

  if (
    input.presentation
      .unresolvedEvidenceCount > 0
  ) {
    blockers.add(
      'PRESENTATION_EVIDENCE_UNRESOLVED',
    );
  }

  if (
    input.presentation
      .conflictingEvidenceCount > 0
  ) {
    blockers.add(
      'PRESENTATION_EVIDENCE_CONFLICTING',
    );
  }

  if (
    input.presentation
      .invalidCompatibilityEvidenceCount > 0
  ) {
    blockers.add(
      'COMPATIBILITY_EVIDENCE_INVALID',
    );
  }

  const reachable =
    input.presentation.reachableMemberIds;

  const missingProjectionMemberIds =
    sorted(
      productionVisibleMemberIds.filter(
        memberId =>
          !reachable.has(memberId),
      ),
    );

  if (
    missingProjectionMemberIds.length > 0
  ) {
    blockers.add(
      'VISIBLE_MEMBER_PROJECTION_MISSING',
    );
  }

  if (
    productionVisibleMemberIds.length !==
    reachable.size
  ) {
    blockers.add(
      'VISIBLE_MEMBER_SET_MISMATCH',
    );
  }

  const missingGeometryRoleMemberIds =
    sorted([
      ...input.geometry
        .missingProjectionMemberIds,
      ...productionVisibleMemberIds.filter(
        memberId =>
          !input.geometry
            .roleByMemberId.has(memberId),
      ),
    ]);

  if (
    missingGeometryRoleMemberIds.length > 0
  ) {
    blockers.add(
      'VISIBLE_MEMBER_GEOMETRY_ROLE_MISSING',
    );
  }

  if (
    input.branch.source ===
    'legacy-fallback'
  ) {
    blockers.add(
      'BRANCH_AUTHORITY_LEGACY_FALLBACK',
    );
  }

  if (!ready(input.branch.readiness)) {
    blockers.add(
      'BRANCH_AUTHORITY_BLOCKED',
    );
  }

  if (
    input.layoutPath.source ===
    'legacy-fallback'
  ) {
    blockers.add(
      'LAYOUT_PATH_LEGACY_FALLBACK',
    );
  }

  if (!ready(input.layoutPath.readiness)) {
    blockers.add(
      'LAYOUT_PATH_BLOCKED',
    );
  }

  if (
    input.topology.selectedSource ===
    'legacy-fallback'
  ) {
    blockers.add(
      'TOPOLOGY_LEGACY_FALLBACK',
    );
  }

  if (
    input.topology.readiness !== 'SAFE'
  ) {
    blockers.add(
      'TOPOLOGY_REPLACEMENT_BLOCKED',
    );
  }

  const missingConnectorEdgeIds =
    sorted(
      input.geometry.roles.flatMap(
        role =>
          role.requiresCanonicalConnector &&
          role.anchorEdgeId &&
          !input.canonicalEdgeIds.has(
            role.anchorEdgeId,
          )
            ? [role.anchorEdgeId]
            : [],
      ),
    );

  if (
    missingConnectorEdgeIds.length > 0
  ) {
    blockers.add(
      'CONNECTOR_EDGE_MISSING',
    );
  }

  const frozenBlockers =
    Object.freeze(
      Array.from(blockers).sort(),
    );

  return Object.freeze({
    status:
      frozenBlockers.length === 0
        ? 'READY'
        : 'BLOCKED',
    blockers: frozenBlockers,
    productionVisibleMemberIds,
    missingProjectionMemberIds,
    missingGeometryRoleMemberIds,
    missingConnectorEdgeIds,
    permitsLegacyFallback: false,
    permitsPartialCanonicalRendering:
      false,
    mutatesCanonicalGraph: false,
  });
};
